import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import type { FundName } from 'api';
import { FirmsService } from 'api';
import {
  FUND_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE,
  FUND_LIST_BY_FIRM_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { SuperAgentError } from 'common/types/services';
import { throwError } from 'utilities';
import {
  GetFundListByFirmParams,
  GetFundListByFirmResponse,
  GetFundListByFirmReturn,
  UseGetFundListByFirmParams,
} from './useGetFundListByFirm.types';

export const getFundListByFirm = async (
  params: GetFundListByFirmParams
): Promise<GetFundListByFirmResponse | null> => {
  const { firmId } = params;

  if (isNil(firmId)) {
    return throwError(FUND_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE);
  }

  try {
    // The backend returns FundName[] directly — the generated paginated type is incorrect.
    const funds = await FirmsService.apiFirmsFundsList(firmId) as unknown as FundName[];
    return funds.map(fund => ({ id: fund.id as number, name: fund.name }));
  } catch (error) {
    // 404 means the firm has no funds yet — treat as an empty list.
    if ((error as SuperAgentError).status === 404) {
      return [];
    }
    return throwError(FUND_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundListByFirm = (params: UseGetFundListByFirmParams): GetFundListByFirmReturn => {
  const { firmId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    GetFundListByFirmReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getFundListByFirm({ firmId }),
    queryKey: [FUND_LIST_BY_FIRM_QUERY_KEY, firmId],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFundListByFirm;
